import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { api, getErrorMessage } from "@/lib/seoApi";
import { useAuth } from "@/lib/auth";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import AuditReport from "@/components/AuditReport";
import { Radar, AlertTriangle, LogIn } from "lucide-react";

const T = {
  en: {
    title: "Sitewide SEO Audit",
    sub: "Scan up to 20 pages of your site in one go. Get an AI-readiness score, prioritized improvements grouped by issue, per-page breakdown, and a downloadable report.",
    gateTitle: "Sign in to run your audit",
    gateBody: "The sitewide audit is free - a free account saves your reports so you can track your progress toward AI-search readiness.",
    gateCta: "Sign in or create a free account",
    gateNote1: "The",
    gateNoteLink1: "generator tools",
    gateNote2: "and",
    gateNoteLink2: "Robots AI checker",
    gateNote3: "stay free without an account.",
    placeholder: "yourwebsite.com",
    run: "Audit site",
    running: "Auditing...",
    hint1: "Pages are discovered from your XML sitemap (or internal links). Analysis is rule-based - no AI guesses. Reports are saved to",
    hintLink: "My audits",
    errTitle: "Audit failed",
    errEmpty: "Please enter your website domain",
    loadingTitle: "Discovering and scanning up to 20 pages...",
    loadingSub: "Usually takes 10-60 seconds depending on the site's speed.",
    emptyBody: "Enter your domain to audit up to 20 pages: overall score, site-level checks (HTTPS, robots.txt, sitemap, llms.txt, AI access), prioritized fixes with affected pages, and a downloadable report.",
    emptyThen: "Then fix issues with the",
    metaGen: "Meta Generator",
    schemaGen: "Schema Generator",
    llmsGen: "llms.txt Generator",
    and: "and",
  },
  fr: {
    title: "Audit SEO complet du site",
    sub: "Analysez jusqu'à 20 pages de votre site d'un coup. Score de préparation IA, améliorations priorisées par problème, détail page par page et rapport téléchargeable.",
    gateTitle: "Connectez-vous pour lancer votre audit",
    gateBody: "L'audit complet est gratuit - un compte gratuit conserve vos rapports pour suivre vos progrès vers la préparation à la recherche IA.",
    gateCta: "Connexion ou création de compte gratuit",
    gateNote1: "Les",
    gateNoteLink1: "outils générateurs",
    gateNote2: "et le",
    gateNoteLink2: "vérificateur Robots IA",
    gateNote3: "restent gratuits sans compte.",
    placeholder: "votresite.com",
    run: "Auditer le site",
    running: "Audit en cours...",
    hint1: "Les pages sont découvertes via votre sitemap XML (ou les liens internes). Analyse par règles - aucune supposition d'IA. Les rapports sont conservés dans",
    hintLink: "Mes audits",
    errTitle: "Échec de l'audit",
    errEmpty: "Veuillez entrer le domaine de votre site",
    loadingTitle: "Découverte et analyse de jusqu'à 20 pages...",
    loadingSub: "Habituellement 10 à 60 secondes selon la vitesse du site.",
    emptyBody: "Entrez votre domaine pour auditer jusqu'à 20 pages : score global, vérifications du site (HTTPS, robots.txt, sitemap, llms.txt, accès IA), correctifs priorisés avec pages affectées, et rapport téléchargeable.",
    emptyThen: "Corrigez ensuite avec le",
    metaGen: "Générateur méta",
    schemaGen: "Générateur de schéma",
    llmsGen: "Générateur llms.txt",
    and: "et le",
  },
};

export default function SiteAudit() {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const { user, ready } = useAuth();
  const { lang } = useLang();
  const t = T[lang];
  const location = useLocation();

  const audit = async (e) => {
    e?.preventDefault();
    if (!url.trim()) {
      setError(t.errEmpty);
      return;
    }
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const { data } = await api.post("/audit/site", { url: url.trim() }, { timeout: 180000 });
      setResult(data);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-10 py-8 sm:py-10">
      <div className="max-w-2xl">
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">{t.title}</h1>
        <p className="mt-2 text-muted-foreground">{t.sub}</p>
      </div>

      {ready && !user ? (
        <div className="mt-8 rounded-lg border p-8 text-center max-w-2xl bg-card" data-testid="site-audit-signin-gate">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-accent text-accent-foreground">
            <LogIn className="h-6 w-6" />
          </div>
          <h2 className="mt-4 text-lg font-heading font-semibold">{t.gateTitle}</h2>
          <p className="mt-2 text-sm text-muted-foreground">{t.gateBody}</p>
          <Button asChild className="mt-5" size="lg" data-testid="site-audit-signin-button">
            <Link to={`/login?next=${encodeURIComponent(location.pathname)}`}>{t.gateCta}</Link>
          </Button>
          <p className="mt-4 text-xs text-muted-foreground">
            {t.gateNote1} <Link to="/meta-generator" className="text-primary underline underline-offset-2">{t.gateNoteLink1}</Link>{" "}
            {t.gateNote2} <Link to="/robots-checker" className="text-primary underline underline-offset-2">{t.gateNoteLink2}</Link> {t.gateNote3}
          </p>
        </div>
      ) : (
        <>
          <form onSubmit={audit} className="mt-6 flex flex-col sm:flex-row gap-3 max-w-2xl">
            <Input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder={t.placeholder}
              className="h-11 bg-card"
              data-testid="site-audit-url-input"
            />
            <Button type="submit" size="lg" disabled={loading || !ready} className="h-11 shrink-0" data-testid="site-audit-run-button">
              <Radar className="h-4 w-4 mr-2" />
              {loading ? t.running : t.run}
            </Button>
          </form>
          <p className="mt-2 text-xs text-muted-foreground">
            {t.hint1} <Link to="/my-audits" className="text-primary underline underline-offset-2">{t.hintLink}</Link>.
          </p>
        </>
      )}

      {error && (
        <Alert variant="destructive" className="mt-6 max-w-2xl" data-testid="site-audit-error">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>{t.errTitle}</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {loading && (
        <div className="mt-8 space-y-4" data-testid="site-audit-loading">
          <div className="rounded-lg border bg-card p-5 flex items-center gap-4">
            <Radar className="h-6 w-6 text-primary animate-pulse shrink-0" />
            <div>
              <p className="text-sm font-medium">{t.loadingTitle}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{t.loadingSub}</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Skeleton className="h-28 rounded-lg" />
            <Skeleton className="h-28 rounded-lg" />
            <Skeleton className="h-28 rounded-lg" />
          </div>
          <Skeleton className="h-72 rounded-lg" />
        </div>
      )}

      {result && <AuditReport result={result} />}

      {!result && !loading && !error && user && (
        <div className="mt-12 rounded-lg border border-dashed p-10 text-center max-w-2xl" data-testid="site-audit-empty-state">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-accent text-accent-foreground">
            <Radar className="h-6 w-6" />
          </div>
          <p className="mt-3 text-sm text-muted-foreground">{t.emptyBody}</p>
          <p className="mt-2 text-xs text-muted-foreground">
            {t.emptyThen} <Link to="/meta-generator" className="text-primary underline underline-offset-2">{t.metaGen}</Link>,{" "}
            <Link to="/schema-generator" className="text-primary underline underline-offset-2">{t.schemaGen}</Link> {t.and}{" "}
            <Link to="/llms-txt" className="text-primary underline underline-offset-2">{t.llmsGen}</Link>.
          </p>
        </div>
      )}
    </div>
  );
}
